/**
 * Phase 3D: Enhanced CPT Reconciliation Table
 * 
 * Shows each CPT with:
 * - Authorization status
 * - Detected evidence (yes/no)
 * - Documentation status (Sufficient/Insufficient/Ambiguous/Not Found)
 * - "View Evidence" button showing snippets
 * - Surgeon confirmation controls
 * 
 * IMPORTANT: Never claims "performed" without surgeon confirmation.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Sparkles,
  ShieldCheck,
  ShieldAlert,
  FileCheck,
  Ban,
  Eye,
  Search,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CptReconciliationEntry,
  CptReconciliationStatus,
  ReconciliationSummary,
  getStatusDisplay,
} from '@/lib/reconciliation/cptStateMachine';
import { CptDetectionResult } from '@/lib/reconciliation/detectionEngine';
import { DocumentationStatusV2 } from '@/lib/phase3/types';
import { EvidenceViewerDialog } from './EvidenceViewerDialog';
import { useDemoMode } from '@/contexts/DemoModeContext';

interface EnhancedCptReconciliationTableProps {
  entries: CptReconciliationEntry[];
  summary: ReconciliationSummary;
  isLoading: boolean;
  isInitializing: boolean;
  needsInitialization: boolean;
  onInitialize: () => Promise<boolean>;
  onConfirmCpt: (
    cptCode: string,
    status: CptReconciliationStatus,
    method: 'direct' | 'clarification_question' | 'demo_auto',
    reason?: string
  ) => Promise<boolean>;
  onConfirmAll: () => Promise<boolean>;
  // Analysis results
  cptAnalysisResults?: CptDetectionResult[];
  fullNoteText?: string;
  hasAnalyzed?: boolean;
  cptDescriptions?: Record<string, string>;
}

function StatusIcon({ status }: { status: CptReconciliationStatus }) {
  switch (status) {
    case 'confirmed_performed':
      return <CheckCircle2 className="w-4 h-4 text-success" />;
    case 'not_performed':
      return <XCircle className="w-4 h-4 text-destructive" />;
    case 'insufficient_documentation':
      return <AlertTriangle className="w-4 h-4 text-warning" />;
    case 'authorized':
    default:
      return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
}

function StatusBadge({ status }: { status: CptReconciliationStatus }) {
  const display = getStatusDisplay(status);
  
  return (
    <Badge
      variant="secondary"
      className={cn(
        "text-xs",
        status === 'confirmed_performed' && "bg-success/10 text-success border-success/30",
        status === 'not_performed' && "bg-destructive/10 text-destructive border-destructive/30",
        status === 'insufficient_documentation' && "bg-warning/10 text-warning border-warning/30",
        status === 'authorized' && "bg-muted text-muted-foreground"
      )}
    >
      {display.label}
    </Badge>
  );
}

function DocumentationStatusBadge({ 
  status, 
  hasEvidence 
}: { 
  status?: DocumentationStatusV2; 
  hasEvidence: boolean;
}) {
  if (!status && !hasEvidence) {
    return (
      <Badge variant="outline" className="text-xs text-muted-foreground">
        <Search className="w-3 h-3 mr-1" />
        Not Analyzed
      </Badge>
    );
  }

  const variants: Record<DocumentationStatusV2, { 
    icon: React.ReactNode; 
    className: string; 
    label: string;
  }> = {
    sufficient: {
      icon: <CheckCircle2 className="w-3 h-3" />,
      className: 'bg-success/10 text-success border-success/30',
      label: 'Sufficient',
    },
    insufficient: {
      icon: <XCircle className="w-3 h-3" />,
      className: 'bg-destructive/10 text-destructive border-destructive/30',
      label: 'Insufficient',
    },
    ambiguous: {
      icon: <AlertTriangle className="w-3 h-3" />,
      className: 'bg-warning/10 text-warning border-warning/30',
      label: 'Ambiguous',
    },
    not_found: {
      icon: <Search className="w-3 h-3" />,
      className: 'bg-muted text-muted-foreground',
      label: 'Not Found',
    },
  };

  const variant = status ? variants[status] : variants.not_found;

  return (
    <Badge variant="outline" className={cn('flex items-center gap-1 text-xs', variant.className)}>
      {variant.icon}
      {variant.label}
    </Badge>
  );
}

function BillableBadge({ isBillable }: { isBillable: boolean }) {
  return (
    <Badge
      variant={isBillable ? 'default' : 'secondary'}
      className={cn(
        "text-xs",
        isBillable && "bg-success text-success-foreground",
        !isBillable && "bg-muted text-muted-foreground"
      )}
    >
      {isBillable ? 'Yes' : 'No'}
    </Badge>
  );
}

interface ReconciliationRowProps {
  entry: CptReconciliationEntry;
  analysisResult?: CptDetectionResult;
  onConfirm: (status: CptReconciliationStatus) => void;
  onViewEvidence: () => void;
  isUpdating: boolean;
  isDemoMode: boolean;
  hasAnalyzed: boolean;
}

function ReconciliationRow({
  entry,
  analysisResult,
  onConfirm,
  onViewEvidence,
  isUpdating,
  isDemoMode,
  hasAnalyzed,
}: ReconciliationRowProps) {
  const [selectedStatus, setSelectedStatus] = useState<CptReconciliationStatus | ''>('');
  const isPending = entry.status === 'authorized';

  // Get documentation status from analysis result or entry
  const documentationStatus = analysisResult?.documentationStatus as DocumentationStatusV2 | undefined;
  const hasEvidence = analysisResult?.detectedInNote ?? entry.documentationFound;

  const handleConfirm = () => {
    if (selectedStatus) {
      onConfirm(selectedStatus);
      setSelectedStatus('');
    }
  };

  return (
    <TableRow className={cn(
      entry.isBillable && "bg-success/5",
      entry.status === 'not_performed' && "bg-destructive/5",
      entry.status === 'insufficient_documentation' && "bg-warning/5"
    )}>
      {/* CPT Code */}
      <TableCell className="font-mono font-medium">
        {entry.cptCode}
      </TableCell>
      
      {/* Description */}
      <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground">
        {entry.cptDescription || '—'}
      </TableCell>
      
      {/* Authorized */}
      <TableCell>
        <Badge variant="outline" className="text-xs">
          <CheckCircle2 className="w-3 h-3 mr-1 text-success" />
          Yes
        </Badge>
      </TableCell>
      
      {/* Detected Evidence */}
      <TableCell>
        <div className="flex items-center gap-2">
          {hasEvidence ? (
            <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/30">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Yes
            </Badge>
          ) : hasAnalyzed ? (
            <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/30">
              <XCircle className="w-3 h-3 mr-1" />
              No
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              <Clock className="w-3 h-3 mr-1" />
              Pending
            </Badge>
          )}
        </div>
      </TableCell>
      
      {/* Documentation Status */}
      <TableCell>
        <DocumentationStatusBadge 
          status={documentationStatus}
          hasEvidence={hasEvidence}
        />
      </TableCell>
      
      {/* View Evidence */}
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onViewEvidence}
                disabled={!analysisResult}
                className="h-8 px-2"
              >
                <Eye className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {analysisResult 
                ? 'View evidence snippets' 
                : 'Run analysis to view evidence'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      
      {/* Surgeon Confirmation Status */}
      <TableCell>
        <div className="flex items-center gap-2">
          <StatusIcon status={entry.status} />
          <StatusBadge status={entry.status} />
          {entry.isDemoEntry && (
            <Badge variant="secondary" className="text-xs">Demo</Badge>
          )}
        </div>
      </TableCell>
      
      {/* Confirmation Action */}
      <TableCell>
        {isPending ? (
          <div className="flex items-center gap-2">
            <Select 
              value={selectedStatus} 
              onValueChange={(v) => setSelectedStatus(v as CptReconciliationStatus)}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed_performed">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-success" />
                    Performed
                  </span>
                </SelectItem>
                <SelectItem value="not_performed">
                  <span className="flex items-center gap-2">
                    <XCircle className="w-3 h-3 text-destructive" />
                    Not Performed
                  </span>
                </SelectItem>
                <SelectItem value="insufficient_documentation">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3 text-warning" />
                    Insuff. Docs
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={handleConfirm}
              disabled={!selectedStatus || isUpdating}
              className="h-8 px-2"
            >
              {isUpdating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3 h-3" />
              )}
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">
            {entry.surgeonConfirmedAt 
              ? new Date(entry.surgeonConfirmedAt).toLocaleDateString()
              : '—'}
          </span>
        )}
      </TableCell>
      
      {/* Billable */}
      <TableCell>
        <BillableBadge isBillable={entry.isBillable} />
      </TableCell>
    </TableRow>
  );
}

export function EnhancedCptReconciliationTable({
  entries,
  summary,
  isLoading,
  isInitializing,
  needsInitialization,
  onInitialize,
  onConfirmCpt,
  onConfirmAll,
  cptAnalysisResults = [],
  fullNoteText,
  hasAnalyzed = false,
  cptDescriptions = {},
}: EnhancedCptReconciliationTableProps) {
  const { isDemoMode } = useDemoMode();
  const [updatingCpt, setUpdatingCpt] = useState<string | null>(null);
  const [isConfirmingAll, setIsConfirmingAll] = useState(false);
  const [selectedCptForEvidence, setSelectedCptForEvidence] = useState<string | null>(null);

  const handleConfirm = async (cptCode: string, status: CptReconciliationStatus) => {
    setUpdatingCpt(cptCode);
    await onConfirmCpt(cptCode, status, isDemoMode ? 'demo_auto' : 'direct');
    setUpdatingCpt(null);
  };

  const handleConfirmAll = async () => {
    setIsConfirmingAll(true);
    await onConfirmAll();
    setIsConfirmingAll(false);
  };

  const getAnalysisResult = (cptCode: string): CptDetectionResult | undefined => {
    return cptAnalysisResults.find(r => r.cptCode === cptCode);
  };

  const selectedAnalysisResult = selectedCptForEvidence 
    ? getAnalysisResult(selectedCptForEvidence) 
    : null;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileCheck className="w-5 h-5" />
            <Skeleton className="h-5 w-48" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Need to initialize entries
  if (needsInitialization) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileCheck className="w-5 h-5 text-primary" />
            CPT Reconciliation
            {isDemoMode && (
              <Badge variant="secondary" className="ml-2 text-xs">Demo Mode</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <ShieldCheck className="w-4 h-4" />
            <AlertTitle>Initialize Reconciliation</AlertTitle>
            <AlertDescription>
              Start the reconciliation process to track evidence and surgeon confirmation for each authorized CPT.
            </AlertDescription>
          </Alert>

          <Button
            onClick={onInitialize}
            disabled={isInitializing}
            className="w-full"
          >
            {isInitializing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Initializing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {isDemoMode ? 'Initialize & Auto-Confirm (Demo)' : 'Initialize Reconciliation'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const canExport = summary.canExport;

  // Calculate analysis stats
  const cptsWithEvidence = cptAnalysisResults.filter(r => r.detectedInNote).length;
  const cptsWithSufficientDocs = cptAnalysisResults.filter(
    r => r.documentationStatus === 'sufficient'
  ).length;

  return (
    <>
      <Card className={cn(
        "border-2",
        canExport ? "border-success/50" : "border-warning/50"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              {canExport ? (
                <ShieldCheck className="w-5 h-5 text-success" />
              ) : (
                <ShieldAlert className="w-5 h-5 text-warning" />
              )}
              CPT Reconciliation Status
              {isDemoMode && (
                <Badge variant="secondary" className="ml-2 text-xs">Demo</Badge>
              )}
            </CardTitle>
            
            {/* Summary badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-xs">
                {summary.totalCpts} CPTs
              </Badge>
              {hasAnalyzed && (
                <Badge 
                  variant="secondary" 
                  className="text-xs bg-primary/10 text-primary"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  {cptsWithEvidence} with Evidence
                </Badge>
              )}
              <Badge 
                variant="secondary" 
                className={cn(
                  "text-xs",
                  summary.billableCpts.length > 0 && "bg-success/10 text-success"
                )}
              >
                {summary.billableCpts.length} Billable
              </Badge>
              {summary.pendingConfirmation > 0 && (
                <Badge variant="secondary" className="text-xs bg-warning/10 text-warning">
                  {summary.pendingConfirmation} Pending
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Status alerts */}
          {canExport ? (
            <Alert className="border-success/50 bg-success/5">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <AlertTitle className="text-success">Ready for Export</AlertTitle>
              <AlertDescription>
                All CPTs have been confirmed. {summary.billableCpts.length} procedure(s) are billable.
              </AlertDescription>
            </Alert>
          ) : summary.pendingConfirmation > 0 ? (
            <Alert variant="destructive" className="bg-warning/10 border-warning/30">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>Surgeon Confirmation Required</AlertTitle>
              <AlertDescription>
                {summary.pendingConfirmation} CPT(s) require surgeon confirmation before export. 
                Evidence detection does NOT confirm performance.
              </AlertDescription>
            </Alert>
          ) : summary.billableCpts.length === 0 ? (
            <Alert variant="destructive">
              <Ban className="w-4 h-4" />
              <AlertTitle>No Billable CPTs</AlertTitle>
              <AlertDescription>
                No procedures are marked as confirmed performed. Export is blocked.
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Demo mode batch confirm */}
          {isDemoMode && summary.pendingConfirmation > 0 && (
            <Button
              variant="outline"
              onClick={handleConfirmAll}
              disabled={isConfirmingAll}
              className="w-full"
            >
              {isConfirmingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Demo: Confirm All as Performed
            </Button>
          )}

          {/* Reconciliation table */}
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[90px]">CPT</TableHead>
                  <TableHead className="w-[140px]">Description</TableHead>
                  <TableHead className="w-[70px]">Auth</TableHead>
                  <TableHead className="w-[90px]">Evidence</TableHead>
                  <TableHead className="w-[100px]">Doc Status</TableHead>
                  <TableHead className="w-[50px]">View</TableHead>
                  <TableHead className="w-[150px]">Surgeon Status</TableHead>
                  <TableHead className="w-[180px]">Confirmation</TableHead>
                  <TableHead className="w-[70px]">Billable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => (
                  <ReconciliationRow
                    key={entry.id}
                    entry={entry}
                    analysisResult={getAnalysisResult(entry.cptCode)}
                    onConfirm={(status) => handleConfirm(entry.cptCode, status)}
                    onViewEvidence={() => setSelectedCptForEvidence(entry.cptCode)}
                    isUpdating={updatingCpt === entry.cptCode}
                    isDemoMode={isDemoMode}
                    hasAnalyzed={hasAnalyzed}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Important notice */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-start gap-2">
            <FileText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Evidence Detection ≠ Confirmation:</strong>{' '}
              The system detects documentation evidence but never claims a procedure was "performed." 
              Only surgeon confirmation can mark a CPT as billable.
            </p>
          </div>

          {/* Export blocking notice */}
          {!canExport && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center gap-2">
              <Ban className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-sm text-destructive">
                <strong>Export Blocked:</strong> Only CPTs with status "Confirmed Performed" 
                can be included in the EHR export.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evidence Viewer Dialog */}
      <EvidenceViewerDialog
        open={!!selectedCptForEvidence}
        onOpenChange={(open) => !open && setSelectedCptForEvidence(null)}
        cptResult={selectedAnalysisResult ?? null}
        fullNoteText={fullNoteText}
      />
    </>
  );
}
